/**
 * DuplicateAlert — inline "this may already exist" notice on a contact/organization form.
 *
 * Two severities, matching the backend's tiering
 * (modules/master_data/contacts/services/duplicate_check_service.py):
 *
 * - `blocking` — the save WILL be rejected (409). Destructive styling, because
 *   this is a dead end until the user changes the field or goes to the record
 *   that already holds it.
 * - `warning` — the save WILL succeed and the pair is queued for review. Warning
 *   styling, and the copy says so: the user is being informed, not stopped.
 *
 * Both name the existing record and link to it. The link opens in a new tab so
 * inspecting the possible duplicate never costs the user the form they are part
 * way through filling in.
 *
 * The `match_type` is surfaced as its own line ("Same phone number") rather than
 * folded into the sentence: the useful question when this appears is not *that*
 * something matched but *which* field matched, since that is what tells the user
 * whether they typed the wrong thing or genuinely found the same {{contact}}.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, CopyCheck } from 'lucide-react';
import { Alert, AlertDescription } from '../../../../components/ui/alert';
import { getEntityUrl } from '../../../../shared/utils/entityUrls';
import { cn } from '../../../../lib/utils';
// One declaration of the shape, from the JSON Schema via orval. `entity_id` /
// `entity_label` are null when the caller may not see the matched record — the
// alert still warns, it just cannot name or link to it.
import type { DuplicateMatch } from '../types';

interface DuplicateAlertProps {
  match: DuplicateMatch | null | undefined;
  entityType: 'contacts' | 'organizations';
  className?: string;
}

// Explicit map (not a dynamic t() key) so a match_type added on the backend
// without a label here is caught in review rather than rendering as a raw token.
const MATCH_TYPE_KEYS: Record<string, string> = {
  email: 'duplicates.signal.email',
  phone: 'duplicates.signal.phone',
  domain: 'duplicates.signal.domain',
  name: 'duplicates.signal.name',
  name_country: 'duplicates.signal.name',
};

export function DuplicateAlert({ match, entityType, className }: DuplicateAlertProps) {
  const { t } = useTranslation(['contacts', 'common']);

  if (!match) return null;

  const isBlocking = match.tier === 'blocking';
  const signalKey = MATCH_TYPE_KEYS[match.match_type];
  const href = match.entity_id
    ? getEntityUrl({
        entityType: entityType === 'contacts' ? 'contacts' : 'companies',
        entityId: match.entity_id,
      })
    : null;
  // No id means the match is real but not ours to name (restricted visibility).
  const label = match.entity_id
    ? match.entity_label?.trim() || t('duplicates.unnamedRecord')
    : t('duplicates.hiddenRecord');

  return (
    <Alert
      variant={isBlocking ? 'destructive' : 'default'}
      className={cn(!isBlocking && 'border-warning/40 bg-warning/10 text-warning', className)}
    >
      {isBlocking ? (
        <AlertTriangle className="h-4 w-4" />
      ) : (
        <CopyCheck className="h-4 w-4" />
      )}
      <AlertDescription className="space-y-1">
        <p className="font-medium">
          {isBlocking
            ? entityType === 'contacts'
              ? t('duplicates.blockedContact')
              : t('duplicates.blockedOrganization')
            : entityType === 'contacts'
              ? t('duplicates.possibleContact')
              : t('duplicates.possibleOrganization')}
        </p>
        {signalKey && <p className="text-xs opacity-80">{t(signalKey)}</p>}
        {/* getEntityUrl returns null for an entity missing from the URL registry.
            Naming the record is still useful then — a dead link is not, so fall
            back to plain text rather than to `#`. */}
        {href ? (
          <Link
            to={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-11 items-center gap-1 font-medium underline underline-offset-2 sm:min-h-0"
          >
            {label}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        ) : (
          <p className="font-medium">{label}</p>
        )}
        {!isBlocking && <p className="text-xs opacity-80">{t('duplicates.queuedForReview')}</p>}
      </AlertDescription>
    </Alert>
  );
}
